import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_PRICING } from "@/lib/seo-defs";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, Award, ShoppingBag, ArrowRight, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function PricingPage() {
  const [plans, setPlans] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => setPlans(r.data?.plans)).catch(() => {});
  }, []);

  const cards = plans
    ? [
        { key: "company_free", role: "company", color: "slate", icon: ShoppingBag, ctaLabel: "Inizia gratis", ctaTo: "/register" },
        { key: "company_pro", role: "company", color: "blue", icon: Sparkles, ctaLabel: "Attiva Company Pro", popular: true, ctaTo: user ? "/app/subscription" : "/register" },
        { key: "company_premium", role: "company", color: "gold", icon: Award, ctaLabel: "Parla con CXOORA", ctaTo: "/contatti" },
        { key: "manager_basic", role: "manager", color: "slate", icon: Star, ctaLabel: "Candidati come manager", ctaTo: "/per-i-manager/candidati" },
        { key: "manager_premium", role: "manager", color: "purple", icon: Sparkles, ctaLabel: "Diventa Manager Premium", ctaTo: user?.role === "manager" ? "/manager/subscription" : "/register?role=manager" },
      ]
    : [];

  return (
    <div className="min-h-screen">
      <SEO {...SEO_PRICING} />
      <PublicHeader />
      <section className="pt-28 pb-16 bg-gradient-to-b from-[#EAF0FF]/30 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">Piani CXOORA</Badge>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              Due modelli, una sola piattaforma.
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              Scegli come collaborare con il network CXOORA: <strong>Marketplace</strong> self-service oppure <strong>Agency Premium</strong> con account manager dedicato.
            </p>
          </div>

          {/* Companies pricing */}
          <div className="mt-12">
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Per le aziende</h2>
            <p className="text-sm text-slate-600 mb-6">Scegli il piano più adatto alla tua esigenza.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {cards.filter((c) => c.role === "company").map((card) => (
                <PricingCard key={card.key} card={card} plan={plans?.[card.key]} navigate={navigate} />
              ))}
            </div>
          </div>

          {/* Managers pricing */}
          <div className="mt-12">
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-1">Per i manager</h2>
            <p className="text-sm text-slate-600 mb-6">Aumenta visibilità e abbassa la fee CXOORA con Premium.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl">
              {cards.filter((c) => c.role === "manager").map((card) => (
                <PricingCard key={card.key} card={card} plan={plans?.[card.key]} navigate={navigate} />
              ))}
            </div>
          </div>

          {/* FAQ-comparativa */}
          <div className="mt-16 cxo-card p-8">
            <h3 className="font-display text-xl font-semibold text-slate-900 mb-4">Marketplace vs Agency Premium — qual è la differenza?</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="p-3 text-left font-semibold text-slate-700">Caratteristica</th>
                    <th className="p-3 text-left font-semibold text-[#2D6BFF]">Marketplace</th>
                    <th className="p-3 text-left font-semibold text-[#B7791F]">Agency Premium</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700">
                  <CompareRow label="Modello legale" mp="Intermediazione (CXOORA è terzo)" ag="Erogazione diretta (CXOORA è fornitore)" />
                  <CompareRow label="Contratto" mp="Azienda ↔ Manager" ag="Azienda ↔ CXOORA + sub-contracting" />
                  <CompareRow label="Selezione manager" mp="Algoritmica, self-service" ag="Curata da account manager dedicato" />
                  <CompareRow label="SLA" mp="Best effort, no penali" ag="SLA contrattuali con penali" />
                  <CompareRow label="NDA" mp="Opzionale" ag="Tri-party obbligatorio" />
                  <CompareRow label="Time-to-project" mp="7-14 giorni" ag="1-3 giorni" />
                  <CompareRow label="Pricing tipico" mp="Da €1.5k/mese + fee 15%" ag="Da €5k/mese + margin 40%" />
                  <CompareRow label="Target azienda" mp="PMI (10-99 dipendenti)" ag="Mid-Market & Large (100+)" />
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-slate-600">Non sei sicuro? Inizia con il <strong>check-up gratuito</strong>: CXOORA ti suggerirà il piano più adatto.</p>
            <Link to="/check-up" className="inline-block mt-3">
              <Button className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white gap-1.5" data-testid="pricing-cta-checkup">
                Fai il check-up gratis <ArrowRight size={14} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const PricingCard = ({ card, plan, navigate }) => {
  if (!plan) return null;
  const Icon = card.icon;
  const isPopular = card.popular;
  const isPremium = card.key === "company_premium";
  const colorMap = {
    slate: { ring: "border-slate-200", text: "text-slate-900", bg: "bg-slate-100" },
    blue: { ring: "border-[#2D6BFF]/30 ring-2 ring-[#2D6BFF]/20", text: "text-[#2D6BFF]", bg: "bg-[#EAF0FF]" },
    gold: { ring: "border-[#B7791F]/30 ring-2 ring-[#B7791F]/15", text: "text-[#B7791F]", bg: "bg-[#FFF6E6]" },
    purple: { ring: "border-[#7c3aed]/30 ring-2 ring-[#7c3aed]/15", text: "text-[#7c3aed]", bg: "bg-[#F3EEFE]" },
  };
  const cm = colorMap[card.color] || colorMap.slate;

  return (
    <div className={`relative cxo-card p-6 ${cm.ring}`} data-testid={`pricing-card-${card.key}`}>
      {isPopular && (
        <span className="absolute -top-3 left-6 px-2.5 py-0.5 bg-[#2D6BFF] text-white text-[11px] font-semibold rounded-full">
          Più scelto
        </span>
      )}
      <div className="flex items-center gap-2">
        <span className={`rounded-xl p-2 ${cm.bg} ${cm.text}`}><Icon size={18} /></span>
        <h3 className="font-display text-lg font-bold text-slate-900">{plan.name}</h3>
      </div>
      <div className="mt-4">
        {plan.is_quote_based ? (
          <p className="font-display text-2xl font-bold text-slate-900">Su misura</p>
        ) : (
          <p>
            <span className="font-display text-3xl font-bold text-slate-900">€{plan.monthly_price.toFixed(0)}</span>
            <span className="text-slate-500 text-sm"> / mese</span>
          </p>
        )}
      </div>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={14} className={`mt-0.5 shrink-0 ${cm.text}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => navigate(card.ctaTo)}
        className={`w-full mt-5 gap-1.5 ${
          card.color === "blue"
            ? "bg-[#2D6BFF] hover:bg-[#1F57F2] text-white"
            : card.color === "gold"
              ? "bg-[#B7791F] hover:bg-[#8B5E12] text-white"
              : card.color === "purple"
                ? "bg-[#7c3aed] hover:bg-[#5b21b6] text-white"
                : "bg-slate-900 hover:bg-slate-800 text-white"
        }`}
        data-testid={`pricing-cta-${card.key}`}
      >
        {card.ctaLabel} <ArrowRight size={14} />
      </Button>
      {isPremium && (
        <p className="text-xs text-slate-500 mt-2 text-center">Quote personalizzato dopo check-up</p>
      )}
    </div>
  );
};

const CompareRow = ({ label, mp, ag }) => (
  <tr className="border-t border-slate-100">
    <td className="p-3 font-medium text-slate-900 text-sm">{label}</td>
    <td className="p-3 text-sm">{mp}</td>
    <td className="p-3 text-sm">{ag}</td>
  </tr>
);
